/**
 * <carousel-component> (Adaptado para Cantu - Sem dependências externas)
 * Inicializa o Swiper nativamente.
 */
class CarouselComponent extends HTMLElement {
  swiper = null;
  #resizeObserver = null;

  connectedCallback() {
    this.#init();
  }

  disconnectedCallback() {
    if (this.swiper && typeof this.swiper.destroy === 'function') {
      this.swiper.destroy(true, true);
    }
    this.swiper = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  async #init() {
    await this.#ensureSwiper();
    if (!this.isConnected || this.swiper) return;
    this.#initSwiper();

    if (window.ResizeObserver) {
      this.#resizeObserver = new ResizeObserver(() => {
        if (this.swiper && typeof this.swiper.update === 'function') this.swiper.update();
      });
      this.#resizeObserver.observe(this);
    }
  }

  #ensureSwiper() {
    return new Promise((resolve) => {
      if (window.Swiper) return resolve();
      let attempts = 0;
      const check = () => {
        if (window.Swiper || attempts >= 60) return resolve();
        attempts += 1;
        requestAnimationFrame(check);
      };
      requestAnimationFrame(check);
    });
  }

  #initSwiper() {
    const container = this.querySelector('.swiper');
    if (!container) return;
    if (!window.Swiper) {
      console.warn('[carousel-component] Swiper indisponível; o carrossel ficará estático.');
      return;
    }

    let options = {};
    const raw = this.getAttribute('data-swiper-options');
    if (raw) {
      try {
        options = JSON.parse(raw);
      } catch (error) {
        console.error('[carousel-component] data-swiper-options JSON inválido:', error);
      }
    }

    const gap = parseInt(this.getAttribute('data-gap'), 10) || 0;
    const desktopItems = parseFloat(this.getAttribute('data-desktop-items')) || 3;
    const mobileItems = parseFloat(this.getAttribute('data-mobile-items')) || 1;

    const defaults = {
      slidesPerView: mobileItems,
      spaceBetween: gap,
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev'),
      },
      pagination: {
        el: this.querySelector('.swiper-pagination'),
        clickable: true,
      },
      loop: this.hasAttribute('data-loop') && this.getAttribute('data-loop') !== 'false',
      autoplay: this.hasAttribute('data-autoplay')
        ? {
            delay: parseInt(this.getAttribute('data-autoplay-delay'), 10) || 3000,
            disableOnInteraction: false,
          }
        : false,
      freeMode: this.hasAttribute('data-free-mode'),
      breakpoints: options.breakpoints || {
        768: { slidesPerView: desktopItems },
      },
      a11y: {
        enabled: true,
        prevSlideMessage: 'Slide anterior',
        nextSlideMessage: 'Próximo slide',
      },
    };

    this.swiper = new window.Swiper(container, { ...defaults, ...options });

    // Dispara o evento avisando que o carrossel está pronto!
    this.dispatchEvent(
      new CustomEvent('carousel:init', { bubbles: true, detail: { swiper: this.swiper } })
    );
  }

  slideNext() {
    if (this.swiper) this.swiper.slideNext();
  }

  slidePrev() {
    if (this.swiper) this.swiper.slidePrev();
  }

  slideTo(index) {
    if (this.swiper) this.swiper.slideTo(index);
  }
}

if (!customElements.get('carousel-component')) {
  customElements.define('carousel-component', CarouselComponent);
}